import React, { useState } from 'react';
import { createBook, getBooks, getBook, updateBook, deleteBook } from '../utils/api';
import ResponseDisplay from './ResponseDisplay';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const BookForm = () => {
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    authorId: '',
    isbn: '',
    publicationYear: '',
    price: '',
    stock: '',
  });
  const [errors, setErrors] = useState({});
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showSampleData, setShowSampleData] = useState(false);

  const validateForm = (action) => {
    const newErrors = {};
    if (!formData.title && action === 'create') newErrors.title = 'Title is required';
    if (!formData.authorId && action === 'create') newErrors.authorId = 'Author ID is required';
    if (!formData.isbn && action === 'create') newErrors.isbn = 'ISBN is required';
    if (!formData.publicationYear && action === 'create') newErrors.publicationYear = 'Publication year is required';
    if (!formData.price && action === 'create') newErrors.price = 'Price is required';
    if (!formData.stock && action === 'create') newErrors.stock = 'Stock is required';
    if (!formData.id && ['get', 'update', 'delete'].includes(action)) {
      newErrors.id = 'Book ID is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const executeAction = async (action) => {
    if (!validateForm(action)) return;
    setLoading(true);
    setLoadingMessage(`${action.charAt(0).toUpperCase() + action.slice(1)} book, please wait... The backend may take up few seconds to wake up.`);

    try {
      let res;
      switch (action) {
        case 'create':
          res = await createBook(formData);
          break;
        case 'getAll':
          res = await getBooks();
          break;
        case 'get':
          res = await getBook(formData.id);
          break;
        case 'update':
          res = await updateBook(formData.id, formData);
          break;
        case 'delete':
          await deleteBook(formData.id);
          res = { data: { message: 'Book deleted successfully' } };
          break;
        default:
          throw new Error('Invalid action');
      }
      setResponse(res.data);
      setError(null);
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} successful!`);
    } catch (err) {
      setError(err.response?.data || { error: `Failed to ${action} book` });
      setResponse(null);
      toast.error(`Failed to ${action} book`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  return (
    <div className="max-w-4xl p-8 mx-auto mt-24 mb-12 transition-all duration-300 bg-white shadow-xl rounded-2xl">
      {loading && <LoadingSpinner message={loadingMessage} />}
      <h2 className="mb-6 text-3xl font-bold text-blue-600">Manage Books</h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {[
          { name: 'id', label: 'Book ID', placeholder: 'Enter Book ID (for Get/Update/Delete)', type: 'text' },
          { name: 'title', label: 'Title', placeholder: 'Enter Book Title', type: 'text' },
          { name: 'authorId', label: 'Author ID', placeholder: 'Enter Author ID', type: 'text' },
          { name: 'isbn', label: 'ISBN', placeholder: 'Enter ISBN', type: 'text' },
          { name: 'publicationYear', label: 'Publication Year', placeholder: 'Enter Year', type: 'number' },
          { name: 'price', label: 'Price', placeholder: 'Enter Price', type: 'number', step: '0.01' },
          { name: 'stock', label: 'Stock', placeholder: 'Enter Stock', type: 'number' },
        ].map((field) => (
          <div key={field.name} className="relative">
            <label className="block mb-1 text-sm font-medium text-gray-700">{field.label}</label>
            <input
              type={field.type}
              name={field.name}
              placeholder={field.placeholder}
              value={formData[field.name]}
              onChange={handleChange}
              step={field.step}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${errors[field.name] ? 'border-red-500' : 'border-gray-300'}`}
              disabled={loading}
              aria-describedby={`${field.name}-error`}
            />
            {errors[field.name] && (
              <p id={`${field.name}-error`} className="mt-1 text-xs text-red-500">{errors[field.name]}</p>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-4 mt-8">
        {[
          { action: 'create', label: 'Create', color: 'bg-blue-600 hover:bg-blue-700' },
          { action: 'getAll', label: 'Get All', color: 'bg-green-500 hover:bg-green-600' },
          { action: 'get', label: 'Get by ID', color: 'bg-yellow-500 hover:bg-yellow-600' },
          { action: 'update', label: 'Update', color: 'bg-orange-500 hover:bg-orange-600' },
          { action: 'delete', label: 'Delete', color: 'bg-red-500 hover:bg-red-600' },
        ].map(({ action, label, color }) => (
          <button
            key={action}
            onClick={() => executeAction(action)}
            disabled={loading}
            className={`${color} text-white px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={label}
          >
            {loading ? 'Processing...' : label}
          </button>
        ))}
      </div>
      <div className="mt-8">
        <button
          onClick={() => setShowSampleData(!showSampleData)}
          className="flex items-center font-semibold text-blue-600 transition-all hover:text-blue-800"
          aria-expanded={showSampleData}
          aria-controls="sample-data"
        >
          {showSampleData ? <FaChevronUp className="mr-2" /> : <FaChevronDown className="mr-2" />}
          Sample Data
        </button>
        {showSampleData && (
          <div id="sample-data" className="p-6 mt-4 bg-gray-50 rounded-xl animate-fade-in">
            <h3 className="mb-4 text-lg font-semibold text-gray-800">Sample Book Data</h3>
            <pre className="p-4 overflow-auto font-mono text-sm text-gray-800 bg-white rounded-lg">
              {JSON.stringify(
                {
                  title: "Harry Potter",
                  authorId: "1",
                  isbn: "978-0-7475-3269-6",
                  publicationYear: 1997,
                  price: 19.99,
                  stock: 100,
                },
                null,
                2
              )}
            </pre>
          </div>
        )}
      </div>
      <ResponseDisplay response={response} error={error} />
    </div>
  );
};

export default BookForm;